import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { ITrainersData, ITrainersTool } from '@/types/trainers';
import { getTools, getTrainersContent } from '@/api/qa';
import DefaultTrainer from '@/components/ui/trainers/DefaultTrainer';
import { TrainerReviewsContextProvider } from '@/hooks/useTrainerReviews';
import { TrainerFeedbackContextProvider } from '@/hooks/useTrainerFeedback';
import UserStore from '@/utils/session';
import PositionAzimuth from '@/components/ui/trainers/PositionAzimuth';
import MosaicTrainer from '@/components/ui/trainers/MosaicTrainer';
import EnumeratorTrainerIcon from '@/components/ui/icons/trainers/EnumeratorTrainerIcon';
import MosaicTrainerIcon from '@/components/ui/icons/trainers/MosaicTrainerIcon';
import BinaryTrainerIcon from '@/components/ui/icons/trainers/BinaryTrainerIcon';
import AzimuthTrainerIcon from '@/components/ui/icons/trainers/AzimuthTrainerIcon';
import MergeTrainerIcon from '@/components/ui/icons/trainers/MergeTrainerIcon';
import PositionTrainerIcon from '@/components/ui/icons/trainers/PositionTrainerIcon';
import SelectTrainerIcon from '@/components/ui/icons/trainers/SelectTrainerIcon';
import RegionPickerTrainer from '@/components/ui/trainers/RegionPickerTrainer';
import { initCapitalize } from '@/utils/strings';
import { TightLayoutNoNav } from '@/components/layout/Layout';
import MergeTrainer from '@/components/ui/trainers/MergeTrainer';
import ImageLocation from '@/components/ui/trainers/ImageLocation';
import ImageQualityTrainer from '@/components/ui/trainers/ImageQualityTrainer';
import GasPriceReading from '@/components/ui/trainers/GasPriceReading';
import TollPriceReading from '@/components/ui/trainers/TollPriceReading';
import HeightRestrictionReading from '@/components/ui/trainers/HeightRestrictionReading';
import HeightRestrictionType from '@/components/ui/trainers/HeightRestrictionType';
import MapPinIcon from '@/components/ui/icons/trainers/MapPinIcon';
import ConfirmOrRelabelImage from '@/components/ui/trainers/ConfirmOrRelabelImage';
import TrainingDataImage from '@/components/ui/trainers/TrainingDataImage';

export enum TrainerType {
  BINARY = 'binary',
  ENUMERATOR = 'enumerator',
  TEXT_ANNOTATION = 'textAnnotation',
  MOSAIC = 'mosaic',
  POINT_OF_INTEREST = 'poi',
  AZIMUTH = 'angular',
  MERGE = 'merge',
  IMAGE_LOCATION = 'imageLocation',
  POSITION_AZIMUTH = 'positionAzimuth',
  IMAGE_QUALITY = 'imageQuality',
  PICKER = 'picker',
  GAS_PRICE_READING = 'gasPriceReading',
  TOLL_PRICE_READING = 'tollPriceReading',
  HEIGHT_RESTRICTION_READING = 'heightRestrictionReading',
  HEIGHT_RESTRICTION_TYPE = 'heightRestrictionType',
  REGION_PICKER = 'regionPicker',
  EVENT_LANES_IMPACTED = 'eventLanesImpacted',
  CONFIRM_OR_RELABEL = 'confirmOrRelabel',
  TRAINING_DATA_IMAGE = 'trainingDataImage',
  TRAINING_DATA_IMAGE_COMPLETENESS = 'trainingDataImageCompleteness',
}

export const getFilterByTrainerType = (
  activeFilter: string,
  trainerType: TrainerType,
) => {
  switch (trainerType) {
    // Enumerator, Image Location, Text Annotation, Gas Price Reading, and Toll Price Reading share the same icon
    case TrainerType.HEIGHT_RESTRICTION_TYPE:
    case TrainerType.HEIGHT_RESTRICTION_READING:
    case TrainerType.TOLL_PRICE_READING:
    case TrainerType.GAS_PRICE_READING:
    case TrainerType.ENUMERATOR:
    case TrainerType.IMAGE_LOCATION:
    case TrainerType.EVENT_LANES_IMPACTED:
    case TrainerType.TEXT_ANNOTATION:
    case TrainerType.CONFIRM_OR_RELABEL:
    case TrainerType.TRAINING_DATA_IMAGE:
    case TrainerType.TRAINING_DATA_IMAGE_COMPLETENESS:
      return (
        activeFilter === TrainerType.ENUMERATOR ||
        activeFilter === TrainerType.TEXT_ANNOTATION ||
        activeFilter === TrainerType.IMAGE_LOCATION ||
        activeFilter === TrainerType.GAS_PRICE_READING ||
        activeFilter === TrainerType.TOLL_PRICE_READING ||
        activeFilter === TrainerType.HEIGHT_RESTRICTION_READING ||
        activeFilter === TrainerType.HEIGHT_RESTRICTION_TYPE ||
        activeFilter === TrainerType.EVENT_LANES_IMPACTED ||
        activeFilter === TrainerType.CONFIRM_OR_RELABEL ||
        activeFilter === TrainerType.TRAINING_DATA_IMAGE ||
        activeFilter === TrainerType.TRAINING_DATA_IMAGE_COMPLETENESS
      );
    case TrainerType.POSITION_AZIMUTH:
    case TrainerType.POINT_OF_INTEREST:
      return (
        activeFilter === TrainerType.POSITION_AZIMUTH ||
        activeFilter === TrainerType.POINT_OF_INTEREST
      );
    case TrainerType.IMAGE_QUALITY:
      return activeFilter === TrainerType.BINARY;
    case TrainerType.PICKER:
      return activeFilter === TrainerType.PICKER;
    case TrainerType.REGION_PICKER:
      return activeFilter === TrainerType.REGION_PICKER;
    case TrainerType.BINARY:
    case TrainerType.MOSAIC:
    case TrainerType.AZIMUTH:
    case TrainerType.MERGE:
      return activeFilter === trainerType;
    default:
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      ((_: never) => null)(trainerType);
      return activeFilter === trainerType;
  }
};

export const getTrainerIcon = (trainerType: TrainerType) => {
  switch (trainerType) {
    // POI and PositionAzimuth share the same icon
    case TrainerType.POINT_OF_INTEREST:
    case TrainerType.POSITION_AZIMUTH:
      return PositionTrainerIcon;
    case TrainerType.MERGE:
      return MergeTrainerIcon;
    case TrainerType.AZIMUTH:
      return AzimuthTrainerIcon;
    // Enumerator, Image Location and Text Annotation share the same icon
    case TrainerType.HEIGHT_RESTRICTION_TYPE:
    case TrainerType.HEIGHT_RESTRICTION_READING:
    case TrainerType.TOLL_PRICE_READING:
    case TrainerType.GAS_PRICE_READING:
    case TrainerType.IMAGE_LOCATION:
    case TrainerType.ENUMERATOR:
    case TrainerType.TEXT_ANNOTATION:
    case TrainerType.EVENT_LANES_IMPACTED:
    case TrainerType.CONFIRM_OR_RELABEL:
    case TrainerType.TRAINING_DATA_IMAGE:
    case TrainerType.TRAINING_DATA_IMAGE_COMPLETENESS:
      return EnumeratorTrainerIcon;
    case TrainerType.MOSAIC:
      return MosaicTrainerIcon;
    case TrainerType.IMAGE_QUALITY:
    case TrainerType.BINARY:
      return BinaryTrainerIcon;
    case TrainerType.PICKER:
      return SelectTrainerIcon;
    case TrainerType.REGION_PICKER:
      return MapPinIcon;
    default:
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      ((_: never) => null)(trainerType);
      // eslint-disable-next-line react/display-name
      return () => <></>;
  }
};

export const getTrainerIconFromTool = (tool?: ITrainersTool) => {
  const trainerType = getTrainerType(tool);
  return getTrainerIcon(trainerType as TrainerType);
};

export const getTrainerName = (tool?: ITrainersTool) => {
  if (!tool) return '';
  return initCapitalize(tool.name);
};

export const getTrainerLocation = (tool?: ITrainersTool) => {
  if (!tool || !tool?.location) return '';
  return tool.location === 'default' ? 'GLOBAL' : tool.location.toUpperCase();
};

export const getTrainersPage = (tool?: ITrainersTool) => {
  if (!tool) return '';
  return (
    '/trainers/' +
    tool.name.replaceAll(' ', '-').toLowerCase() +
    `?location=${tool.location}`
  );
};

export const getTrainerType = (tool?: ITrainersTool) => {
  if (!tool) return '';
  return tool.widget;
};

const TrainerPage: React.FC = () => {
  const [data, setData] = useState<ITrainersData | undefined>();
  const [tool, setTool] = useState<ITrainersTool>();
  const [tools, setTools] = useState<ITrainersTool[]>([]);
  const name = getTrainerName(tool);
  const location = getTrainerLocation(tool);
  const page = getTrainersPage(tool);
  const description = tool?.args?.description || '';
  const props = { data, name, location, page, description, tool, tools };
  const trainerType = getTrainerType(tool) as TrainerType;
  const router = useRouter();

  useEffect(() => {
    const tryToGetTool = async () => {
      const id = router.query.id as string;
      const location = (
        (router.query.location || 'default') as string
      ).toLowerCase();
      const toolName = id.replaceAll('-', ' ').toLowerCase();

      const { tools } = await getTools();
      const tool: ITrainersTool | undefined = (tools || [])?.find(
        (t: ITrainersTool) =>
          t.name?.toLowerCase() === toolName &&
          t.location?.toLowerCase() === location,
      );
      if (!tool || tool.disabled) {
        return router.push('/trainers');
      }

      const data = await getTrainersContent(tool._id, `/trainers/${id}`);
      if (data) {
        if ('redirect' in data && data.redirect)
          setData({
            ...data,
          });
        // Important do not return other data for redirects
        else if (data.error) setData({ ...data });
        else setData({ ...data });
        setTool(tool);
        setTools(tools);
      }
    };
    tryToGetTool();
  }, []);

  let Content = <></>;

  const eventQueues = ['eventRegionPicker', 'eventAttributes'];
  if (tool?.queue && eventQueues.includes(tool?.queue)) {
    Content = <RegionPickerTrainer {...props} />;
  } else {
    switch (trainerType) {
      case TrainerType.PICKER:
      case TrainerType.POINT_OF_INTEREST:
        Content = (
          <PositionAzimuth
            {...props}
            modifyPosition={true}
            modifyAzimuth={false}
          />
        );
        break;
      case TrainerType.POSITION_AZIMUTH:
        Content = (
          <PositionAzimuth
            {...props}
            modifyPosition={true}
            modifyAzimuth={true}
          />
        );
        break;
      case TrainerType.MERGE:
        Content = <MergeTrainer {...props} />;
        break;
      case TrainerType.AZIMUTH:
        Content = (
          <PositionAzimuth
            {...props}
            modifyPosition={false}
            modifyAzimuth={true}
          />
        );
        break;
      case TrainerType.IMAGE_QUALITY:
        Content = <ImageQualityTrainer {...props} />;
        break;
      case TrainerType.GAS_PRICE_READING:
        Content = <GasPriceReading {...props} />;
        break;
      case TrainerType.TOLL_PRICE_READING:
        Content = <TollPriceReading {...props} />;
        break;
      case TrainerType.HEIGHT_RESTRICTION_READING:
        Content = <HeightRestrictionReading {...props} />;
        break;
      case TrainerType.HEIGHT_RESTRICTION_TYPE:
        Content = <HeightRestrictionType {...props} />;
        break;
      case TrainerType.IMAGE_LOCATION:
        Content = <ImageLocation {...props} />;
        break;
      case TrainerType.ENUMERATOR:
      case TrainerType.TEXT_ANNOTATION:
      case TrainerType.BINARY:
        Content = <DefaultTrainer {...props} />;
        break;
      case TrainerType.MOSAIC:
        Content = <MosaicTrainer {...props} />;
        break;
      case TrainerType.EVENT_LANES_IMPACTED:
      case TrainerType.REGION_PICKER:
        Content = <RegionPickerTrainer {...props} />;
        break;
      case TrainerType.CONFIRM_OR_RELABEL:
        Content = <ConfirmOrRelabelImage {...props} />;
        break;
      case TrainerType.TRAINING_DATA_IMAGE:
        Content = <TrainingDataImage {...props} variant={'correctness'} />;
        break;
      case TrainerType.TRAINING_DATA_IMAGE_COMPLETENESS:
        Content = <TrainingDataImage {...props} variant={'completeness'} />;
        break;
      default: {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        ((_: never) => null)(trainerType);
        break;
      }
    }
  }

  return (
    <TightLayoutNoNav>
      <TrainerReviewsContextProvider>
        <TrainerFeedbackContextProvider>
          {Content}
        </TrainerFeedbackContextProvider>
      </TrainerReviewsContextProvider>
    </TightLayoutNoNav>
  );
};

export default TrainerPage;

export const getServerSideProps = () => {
  const user = UserStore.get();
  const isProhibited =
    !user || (user.isEmailUser && !user.admin && !user.advancedReviewer);

  if (isProhibited) {
    return {
      redirect: {
        destination: `/connect-wallet?redirectTo=/trainers`,
        permanent: false,
      },
    };
  }

  return {
    props: {},
  };
};
